import { useCallback, useEffect, useRef } from 'react';
import { track } from '../utils/mixpanelTracking';

/**
 * Hook for tracking user interactions and component lifecycle events with Mixpanel
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.componentName - Name of the component for tracking
 * @param {Object} options.properties - Additional properties to include in events
 * @param {boolean} options.trackMount - Whether to track component mount
 * @param {boolean} options.trackUnmount - Whether to track component unmount
 * @param {boolean} options.trackUpdates - Whether to track component updates
 * @param {Array} options.updateDeps - Dependencies to watch for updates
 * @returns {Object} - Tracking methods
 */
const useMixpanelTracking = ({
  componentName,
  properties = {},
  trackMount = false,
  trackUnmount = false,
  trackUpdates = false,
  updateDeps = []
} = {}) => {
  const mountedRef = useRef(false);
  const prevDepsRef = useRef(updateDeps);
  
  // Track component mount
  useEffect(() => {
    if (trackMount) {
      track('Component Mounted', {
        component_name: componentName,
        ...properties
      });
    }
    
    mountedRef.current = true;
    
    // Track component unmount
    return () => {
      if (trackUnmount) {
        track('Component Unmounted', {
          component_name: componentName,
          ...properties
        });
      }
    };
  }, [componentName, properties, trackMount, trackUnmount]);
  
  // Track component updates
  useEffect(() => {
    if (!mountedRef.current || !trackUpdates) return;
    
    // Skip the first render
    const prevDeps = prevDepsRef.current;
    prevDepsRef.current = updateDeps;
    
    // Find which dependency changed
    const changedDeps = updateDeps.reduce((acc, dep, index) => {
      if (dep !== prevDeps[index]) {
        acc.push({
          index,
          prev_value: typeof prevDeps[index] === 'object' ? 'object' : prevDeps[index],
          new_value: typeof dep === 'object' ? 'object' : dep
        });
      }
      return acc;
    }, []);
    
    if (changedDeps.length > 0) {
      track('Component Updated', {
        component_name: componentName,
        changed_dependencies: changedDeps,
        ...properties
      });
    }
  }, [componentName, properties, trackUpdates, ...updateDeps]);
  
  // Track custom events
  const trackEvent = useCallback((eventName, eventProperties = {}) => {
    track(eventName, {
      component_name: componentName,
      ...properties,
      ...eventProperties
    });
  }, [componentName, properties]);
  
  // Track user interactions
  const trackInteraction = useCallback((interactionType, interactionProperties = {}) => {
    track(`User Interaction: ${interactionType}`, {
      component_name: componentName,
      interaction_type: interactionType,
      timestamp: new Date().toISOString(),
      ...properties,
      ...interactionProperties
    });
  }, [componentName, properties]);
  
  // Create tracking event handlers
  const createTrackingHandler = useCallback((eventName, handler, eventProperties = {}) => {
    return (...args) => {
      trackEvent(eventName, eventProperties);
      if (handler) {
        return handler(...args);
      }
    };
  }, [trackEvent]);
  
  return {
    trackEvent,
    trackInteraction,
    createTrackingHandler
  };
};

export default useMixpanelTracking;
